import { EditIcon, TrashCanIcon } from 'features/compositions/assets/icons/icons';
import { useCompositionsContext } from '../../context/CompositionsContext';
import { useModalSystem } from 'config/context/ModalSystemContext';
import styles from './CompositionTableToolbar.module.css';
import { useContext, useState } from 'react';
import TablePagination from './table-pagination/TablePagination';
import ThemeContext from 'config/context/themeContext';
import CompositionTableSettings from './composition-table-settings/CompositionTableSettings';
import AddPrivateKeyButton from 'features/permissions/components/authorization-guard/AddPrivateKeyButton';
import { useDropRowsService } from '../../services/useDropRowsService';
import { useUpdateRowsService } from '../../services/useUpdateRowsService';
import { ExportIcon } from 'assets/icons';
import downloadFile from 'lib/DownloadFile';
import { fromScaledInteger } from '../../utils/decimalScale';
import { useIsPluginActive } from 'features/plugins';

const CompositionTableToolbar = ({ editMode, setEditMode }) => {
  const { dark } = useContext(ThemeContext);
  const { openModal } = useModalSystem();
  const [deleteMode, setDeleteMode] = useState(false);
  const { selectedCount, tempPrivateKey, setTempPrivateKey, selectedRows, selectedComposition, clearSelection, editedRowsData, clearEditedRows } = useCompositionsContext();
  const { dropRows, loading, error, resetServerStatus } = useDropRowsService();
  const { updateRows, loading: updateLoading, error: updateError, resetServerStatus: resetUpdateStatus } = useUpdateRowsService();

  const isPluginActive = useIsPluginActive(5);

  const hasSelectedRows = selectedCount > 0;

  const handleEditClick = () => {
    setEditMode(true);
    setDeleteMode(false);
  };

  const handleDeleteClick = () => {
    setDeleteMode(true);
    setEditMode(false);
  };

  const handleCancel = () => {
    setEditMode(false);
    setDeleteMode(false);
    clearEditedRows();
    resetServerStatus();
    resetUpdateStatus();
  };

  const handleRemove = async () => {
    try {
      await dropRows({
        composition: selectedComposition.name,
        keys: selectedRows,
        permission: selectedComposition?.permission,
        private_key: tempPrivateKey
      });

      // Reset states after successful deletion
      setDeleteMode(false);
      clearSelection();
    } catch (error) {
      console.error('Error deleting rows:', error);
    }
  };

  const handleSave = async () => {
    try {
      const primaryKeyName = selectedComposition.structure.indexes[0]?.name;
      if (!primaryKeyName) {
        setEditMode(false);
        return;
      }

      const rowsToUpdate = Object.entries(editedRowsData).map(([rowId, changes]) => {
        const normalizedId = isNaN(rowId) ? rowId : Number(rowId);
        const originalRow = (selectedComposition.rows || []).find(r => r[primaryKeyName] === normalizedId);
        if (!originalRow) return null;

        const completeRow = {};

        if (selectedComposition?.structure?.indexes) {
          selectedComposition.structure.indexes.forEach(index => {
            completeRow[index.name] = changes[index.name] !== undefined
              ? changes[index.name]
              : originalRow[index.name];
          });
        }
        if (selectedComposition?.structure?.fields) {
          selectedComposition.structure.fields.forEach(field => {
            completeRow[field.name] = changes[field.name] !== undefined
              ? changes[field.name]
              : originalRow[field.name];
          });
        }

        return completeRow;
      }).filter(row => row !== null);

      if (rowsToUpdate.length === 0) {
        setEditMode(false);
        return;
      }

      await updateRows({
        composition: selectedComposition.name,
        rowsData: rowsToUpdate,
        permission: selectedComposition?.permission,
        private_key: tempPrivateKey
      });

      setEditMode(false);
      clearEditedRows();
      clearSelection();
    } catch (error) {
      console.error('Error updating rows:', error);
    }
  };

  const handleExport = () => {
    openModal('EXPORT_DATA', { onConfirm: handleExportConfirm });
  };

  const handleExportConfirm = (format) => {
    if (!selectedComposition || !selectedRows || selectedRows.length === 0) {
      return;
    }

    // Get primary key name
    const primaryKeyName = selectedComposition.structure?.indexes?.[0]?.name;
    if (!primaryKeyName) {
      console.error('Primary key not found');
      return;
    }

    // Filter rows based on selected primary keys
    const filteredRows = selectedComposition.rows.filter(row => {
      const rowKey = row[primaryKeyName];
      // Handle both string and number comparisons
      return selectedRows.some(selectedKey => {
        const normalizedRowKey = typeof rowKey === 'number' ? rowKey : Number(rowKey);
        const normalizedSelectedKey = typeof selectedKey === 'number' ? selectedKey : Number(selectedKey);
        return normalizedRowKey === normalizedSelectedKey || rowKey === selectedKey;
      });
    });

    if (filteredRows.length === 0) {
      console.error('No rows found for selected keys');
      return;
    }

    // Tačne decimale se na lancu čuvaju kao ceo broj u najmanjoj jedinici. Bez
    // ovoga bi izvoz nosio cente (`797868`) umesto iznosa (`7978.68`) — i to u
    // fajlu koji dalje ide u tuđu tabelu, gde niko ne zna za `scale`.
    const scales = [
      ...(selectedComposition.structure?.indexes || []),
      ...(selectedComposition.structure?.fields || []),
    ].filter((f) => Number.isInteger(f?.scale));

    const exportRows = scales.length === 0 ? filteredRows : filteredRows.map((row) => {
      const out = { ...row };
      for (const f of scales) {
        if (out[f.name] !== undefined && out[f.name] !== null && out[f.name] !== '') {
          out[f.name] = fromScaledInteger(out[f.name], f.scale);
        }
      }
      return out;
    });

    // Generate filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const safeCompositionName = selectedComposition.name.replace(/[^a-zA-Z0-9_]/g, '_');
    const filename = `${safeCompositionName}_${selectedComposition?.selected_scope}_export_${timestamp}.${format}`;

    // Format and download based on format
    switch (format) {
      case 'json':
        downloadFile(exportRows, filename, { type: 'application/json' });
        break;

      case 'csv':
        // Get all column names (indexes + fields)
        const columns = [
          ...(selectedComposition.structure?.indexes || []).map(idx => idx.name),
          ...(selectedComposition.structure?.fields || []).map(fld => fld.name)
        ];

        // Create CSV header
        const csvHeader = columns.join(',');

        // Create CSV rows
        const csvRows = exportRows.map(row => {
          return columns.map(col => {
            const value = row[col];
            // Handle values that might contain commas, quotes, or newlines
            if (value === null || value === undefined) {
              return '';
            }
            const stringValue = String(value);
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',');
        });

        const csvContent = [csvHeader, ...csvRows].join('\n');
        downloadFile(csvContent, filename, { type: 'text/csv;charset=utf-8;' });
        break;

      case 'xml':
        // Create XML structure
        const xmlHeader = '<?xml version="1.0" encoding="UTF-8"?>\n';
        const rootElement = safeCompositionName.replace(/[^a-zA-Z0-9_]/g, '_');
        
        const xmlRows = exportRows.map((row, index) => {
          const rowElement = `  <row id="${index}">\n`;
          const rowData = Object.entries(row).map(([key, value]) => {
            const safeKey = key.replace(/[^a-zA-Z0-9_]/g, '_');
            // Escape XML special characters
            const escapedValue = String(value || '')
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&apos;');
            return `    <${safeKey}>${escapedValue}</${safeKey}>\n`;
          }).join('');
          return rowElement + rowData + '  </row>\n';
        }).join('');

        const xmlContent = xmlHeader + `<${rootElement}>\n${xmlRows}</${rootElement}>`;
        downloadFile(xmlContent, filename, { type: 'application/xml;charset=utf-8;' });
        break;

      default:
        console.error('Unsupported export format:', format);
    }
  };

  return (
    <div className={`${styles.composition_table_toolbar} ${dark ? styles.dark : ''}`}>
      <div className={styles.container}>
        <CompositionTableSettings />

        <TablePagination dark={dark} />

        <div className={styles.actions}>
          {
            AddPrivateKeyButton({ permission: selectedComposition?.permission, setTempPrivateKey, hasTempKey: (!!tempPrivateKey) }) || (
              <>
                {
                  isPluginActive && 
                    <button className={styles.export_button} disabled={!hasSelectedRows} onClick={handleExport}>
                      <ExportIcon />
                    </button>
                }
                {deleteMode && (
                  <div className={styles.decision_buttons}>
                    <button
                      className={styles.decision_btn__red}
                      onClick={handleRemove}
                      disabled={loading}
                    >
                      {loading ? 'Removing...' : 'Remove'}
                    </button>
                    <button className={styles.decision_btn__black} onClick={handleCancel}>Decline</button>
                  </div>
                )}

                {editMode && (
                  <div className={styles.decision_buttons}>
                    <button className={styles.decision_btn__black} onClick={handleCancel}>Cancel</button>
                    <button
                      className={styles.decision_btn__blue}
                      onClick={handleSave}
                      disabled={updateLoading}
                    >
                      {updateLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}

                {!editMode && !deleteMode && (
                  <div className={styles.action_buttons}>
                    <button disabled={!hasSelectedRows} onClick={handleEditClick}>
                      <EditIcon />
                    </button>
                    <button disabled={!hasSelectedRows} onClick={handleDeleteClick}>
                      <TrashCanIcon />
                    </button>
                  </div>
                )}
              </>
            )
          }
        </div>
      </div>
      {(error || updateError) && (
        <div className={styles.error_message}>
          Error: {error || updateError}
        </div>
      )}
    </div>
  )
}

export default CompositionTableToolbar;
